import { useState, MouseEvent } from 'react';

import { ChatMessageTemplateProps, MessageModel } from '@progress/kendo-react-conversational-ui';
import { classNames } from '@progress/kendo-react-common';
import { Button } from '@progress/kendo-react-buttons';

import { useAppDispatch, useAppSelector } from '../../../../common/hooks/redux';
import { selectUser } from '../../../../common/store/slices/auth.slice';
import { KWELLO_ICON, KwelloSvgIcon } from '../../../../common/components/icon/KwelloSvgIcon';
import {
  selectConversationId,
  setCitations,
  setCitationsOrder,
  setIsPromptIdeasOpen,
  setTargetCitation,
  toggleCitations,
} from '../../../../common/store/slices/elsie-ai.slice';
import { useSendFeedbackMutation } from '../../../../common/store/api/elsie.api';
import WidgetAiMessageFeedback from './WidgetAiMessageFeedback';
import { ServerThinkingCard } from './ServerThinking';
import { ServerErrorCard } from './ServerError';
import WidgetAiTextWithCitations from './WidgetAiTextWithCitations';
import { detailPaths } from '../../../../modules/insights/constants';
import { useLocation } from 'react-router-dom';
import useApiHeaders from 'common/hooks/useApiHeaders';

interface MessageMeta {
  meta?: {
    citations: string[];
    citationsOrder: Map<string, number>;
  };
}

interface WidgetAiMessageTemplateProps extends ChatMessageTemplateProps {
  item: MessageModel & MessageMeta;
}

export default function WidgetAiMessageTemplate({ item }: WidgetAiMessageTemplateProps) {
  const dispatch = useAppDispatch();

  const [sendFeedback] = useSendFeedbackMutation();
  const conversationId =
    useAppSelector(selectConversationId) || 'a000a12a-0000-0000-aaaa-abc000000a0a';

  const apiHeaders = useApiHeaders();

  const [feedback, setFeedback] = useState<boolean | undefined>();
  const [isFeedbackVisible, setIsFeedbackVisible] = useState<boolean>(false);

  const { meta } = item;

  const user = useAppSelector(selectUser);

  const location = useLocation();
  const pathname = location.pathname;
  const isDetailsPage = detailPaths.find((path) => pathname.endsWith(path));

  const onFeedback = (isPositive: boolean) => {
    setFeedback(isPositive);
    setIsFeedbackVisible(true);

    void sendFeedback({
      headers: apiHeaders,
      payload: {
        conversationId,
        userId: user.id ?? '0',
        rating: isPositive ? 'thumbs up' : 'thumbs down',
        reason: [],
        comments: '',
        freeText: '',
      },
    });
  };

  const onCloseFeedback = () => {
    setIsFeedbackVisible(false);
  };

  const onCopy = () => {
    void navigator.clipboard.writeText(item.text ?? '');
  };

  const onOpenCitations = (
    event: MouseEvent<HTMLButtonElement> | MouseEvent<HTMLAnchorElement>
  ) => {
    event?.preventDefault();

    if (!meta) {
      return;
    }

    const citationNumber = (event.target as HTMLElement)?.dataset.citation;

    if (citationNumber) {
      const citation = meta.citations.find(
        (citation, index) => index + 1 === Number(citationNumber)
      );
      dispatch(setTargetCitation({ targetCitation: citation }));
    } else {
      dispatch(setTargetCitation({ targetCitation: undefined }));
    }

    dispatch(setIsPromptIdeasOpen({ isPromptIdeasOpen: false }));
    dispatch(setCitations({ citations: meta.citations }));
    dispatch(setCitationsOrder({ citationsOrder: meta.citationsOrder }));
    dispatch(toggleCitations({ isCitationsOpen: true }));
  };

  return (
    <div
      className={classNames({
        ['widget-ai-message-template-sender']: item.author.id === user.email,
        ['widget-ai-message-template-response']: item.author.id !== user.email,
        ['widget-ai-message-template-response-detail-page']:
          item.author.id !== user.email && isDetailsPage,
      })}
    >
      {!item.status && item.text && (
        <WidgetAiTextWithCitations text={item.text} onOpenCitations={onOpenCitations} />
      )}
      {item.author.id !== user.email && !item.typing && !item.status && (
        <div className="widget-ai-message-template-actions">
          <div>
            <Button
              className="widget-ai-secondary-button"
              themeColor="light"
              onClick={onOpenCitations}
              disabled={!meta?.citations?.length}
              rounded={'full'}
            >
              <KwelloSvgIcon icon={KWELLO_ICON.CITATIONS} />
              Citations
            </Button>
            <Button
              className="widget-ai-secondary-button"
              themeColor="light"
              onClick={onCopy}
              rounded={'full'}
            >
              <KwelloSvgIcon icon={KWELLO_ICON.COPY_ICON} />
              Copy
            </Button>
          </div>
          <div>
            <Button
              className={classNames('widget-ai-rate-button', { ['selected']: feedback === true })}
              fillMode="flat"
              onClick={() => onFeedback(true)}
            >
              <KwelloSvgIcon icon={KWELLO_ICON.THUMBS_UP_ICON} />
            </Button>
            <Button
              className={classNames('widget-ai-rate-button', {
                ['selected']: feedback === false,
              })}
              fillMode="flat"
              onClick={() => onFeedback(false)}
            >
              <KwelloSvgIcon icon={KWELLO_ICON.THUMBS_DOWN_ICON} />
            </Button>
          </div>
        </div>
      )}
      {isFeedbackVisible && (
        <WidgetAiMessageFeedback onClose={onCloseFeedback} isPositive={!!feedback} />
      )}
      {item.typing && (
        <div className="widget-ai-message-template-generating">
          <ServerThinkingCard />
        </div>
      )}
      {(item.status?.includes('40') || item.status?.includes('50')) && (
        <div className="widget-ai-message-error">
          <ServerErrorCard />
        </div>
      )}
      {item.status && (!item.status?.includes('40') || !item.status?.includes('50')) && (
        <div className="widget-ai-message-error">
          <ServerErrorCard text={item.text} />
        </div>
      )}
    </div>
  );
}
